import React, { useEffect, useRef } from 'react';
import { Navigate, useLocation, Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useSubscription } from '@/hooks/useSubscription';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Lock } from 'lucide-react';

interface ProtectedSubscribedRouteProps {
  children: React.ReactNode;
}

const ProtectedSubscribedRoute: React.FC<ProtectedSubscribedRouteProps> = ({ children }) => {
  const { user, loading } = useAuth();
  const { isSubscribed, isAdmin, isBreeder } = useSubscription();
  const location = useLocation();

  // Les admins et éleveurs ont accès complet sans abonnement
  const hasAccess = isAdmin || isSubscribed || isBreeder;

  // 🔐 SEC: Toast dans useEffect pour éviter le spam lors des re-renders
  // ✅ useRef/useEffect AVANT tout return conditionnel (React Rules of Hooks)
  const toastShownRef = useRef(false);
  useEffect(() => {
    if (!hasAccess && user && !toastShownRef.current) {
      toast.warning('Fonctionnalité réservée aux abonnés.');
      toastShownRef.current = true;
    }
  }, [hasAccess, user]);

  // SÉCURITÉ: Aucun bypass automatique autorisé
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Vérification de l'abonnement...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/auth" state={{ from: location }} replace />;
  }

  if (!hasAccess) {

    // Show inline message with CTA instead of immediate redirect
    return (
      <div className="min-h-[60vh] flex items-center justify-center px-4">
        <Card className="max-w-xl w-full">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Lock className="h-5 w-5" />
              Contenu réservé aux abonnés payants
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-muted-foreground mb-4">
              Cette fonctionnalité est disponible avec un abonnement actif. Découvrez nos offres pour débloquer le Forum et l'Assistant Vétérinaire IA.
            </p>
            <div className="flex flex-wrap gap-3">
              <Button asChild>
                <Link to="/subscription" state={{ from: location }}>
                  Voir les abonnements
                </Link>
              </Button>
              <Button variant="outline" asChild>
                <Link to="/">
                  Retour à l'accueil
                </Link>
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return <>{children}</>;
};

export default ProtectedSubscribedRoute;